import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Newspaper, Search, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HERO_BG = "https://images.unsplash.com/photo-1558465202-92356bf74344?crop=entropy&cs=srgb&fm=jpg&q=85";

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news?limit=50`);
        setNews(response.data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const filteredNews = news.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (article.excerpt && article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || article.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getCategoryBadge = (category) => {
    const styles = {
      news: "bg-[#009E60] text-white",
      match_report: "bg-[#FFC72C] text-black",
      press_release: "bg-gray-500 text-white"
    };
    const labels = {
      news: "News",
      match_report: "Match Report",
      press_release: "Press Release"
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold uppercase ${styles[category] || styles.news}`}>
        {labels[category] || category}
      </span>
    );
  };

  return (
    <div data-testid="news-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Newspaper className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            News & Updates
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Stay informed with the latest news, match reports, and press releases from ABFA.
          </p>
          
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 max-w-2xl mx-auto">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 bg-white/10 border-0 text-white placeholder:text-gray-500 rounded-none"
                data-testid="news-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48 rounded-none bg-white/10 border-0 text-white" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="news">News</SelectItem>
                <SelectItem value="match_report">Match Reports</SelectItem>
                <SelectItem value="press_release">Press Releases</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-video w-full" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </div>
          ) : filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((article, i) => (
                <Link 
                  key={article.article_id} 
                  to={`/news/${article.slug || article.article_id}`}
                  className={`card-abfa group ${i === 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}
                  data-testid={`news-card-${article.article_id}`}
                >
                  <div className={`${i === 0 ? 'aspect-[2/1]' : 'aspect-video'} overflow-hidden`}>
                    <img 
                      src={article.image_url || HERO_BG} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {getCategoryBadge(article.category)}
                      <span className="text-sm text-gray-500">{formatDate(article.published_at)}</span>
                    </div>
                    <h3 className={`font-heading font-bold uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors mb-2 ${i === 0 ? 'text-2xl' : 'text-lg'}`}>
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">{article.excerpt}</p>
                    )}
                    <div className="flex items-center text-[#009E60] font-medium text-sm group-hover:translate-x-2 transition-transform">
                      Read More <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">
                {searchQuery || categoryFilter !== "all" ? "No Results Found" : "No News Articles"}
              </h3>
              <p className="text-gray-500">
                {searchQuery || categoryFilter !== "all" 
                  ? "Try adjusting your search or filter criteria." 
                  : "Check back soon for the latest updates."}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
